import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  Car, 
  User, 
  Calendar, 
  AlertCircle, 
  CheckCircle, 
  Clock, 
  Wrench,
  Brain,
  FileText,
  Phone,
  Mail,
  RefreshCw,
  Eye,
  Settings,
  TrendingUp,
  Activity,
  DollarSign,
  Calculator,
  Send,
  Hash,
  Tag,
  Users,
  MapPin,
  X,
  Download,
  Save,
  AlertTriangle
} from 'lucide-react';
import toast from 'react-hot-toast';
import { jobAPI, apiUtils } from '../../services/api';
import { useJobDashboard } from './hooks/useJobDashboard';
import { useJobs } from './hooks/useJobs';
import { useCustomers } from './hooks/useCustomers';
import { useDiagnostics } from './hooks/useDiagnostics';
import { useMechanicPreferences } from './hooks/useMechanicPreferences';
import { useDocuments } from './hooks/useDocuments';
import { useJobStatus } from './hooks/useJobStatus';
import { useInvoiceBuilder } from './hooks/useInvoiceBuilder';
import { useDiagnosisFlow } from './hooks/useDiagnosisFlow';
import { useInquiries } from './hooks/useInquiries';
import EnhancedDiagnosisModal from './modals/EnhancedDiagnosisModal';
import SettingsTab from './tabs/SettingsTab';
import DashboardTab from './tabs/DashboardTab';
import JobsTab from './tabs/JobsTab';
import CustomersTab from './tabs/CustomersTab';
import DiagnosticsTab from './tabs/DiagnosticsTab';
import DocumentsTab from './tabs/DocumentsTab';
import InquiriesTab from './tabs/InquiriesTab';
import CreateJobModal from './modals/CreateJobModal';
import JobDetailsModal from './modals/JobDetailsModal';
import StatusUpdateModal from './modals/StatusUpdateModal';
import InvoiceModal from './modals/InvoiceModal';
import CustomerJobsModal from './modals/CustomerJobsModal';
import DiagnosticDetailModal from './modals/DiagnosticDetailModal';
import QuickIntakeModal from './modals/QuickIntakeModal';
import ConvertInquiryModal from './modals/ConvertInquiryModal';
import DiagnosisProgressModal from './modals/DiagnosisProgressModal';
import { getStatusColor, getPriorityColor } from './parts/statusStyles';
import { messageFromError } from './parts/errorMessage';
import Button from '../common/Button';
import PageHeader from '../common/PageHeader';

// Tab validation and aliasing
const VALID_TABS = ['dashboard', 'jobs', 'customers', 'documents', 'diagnostics', 'inquiries', 'settings'];
// /invoices routes here; "invoices" is a Documents sub-tab, not a tab.
const TAB_ALIASES = { invoices: 'documents' };

const TABS = [
  { id: 'dashboard', name: 'Overview', icon: TrendingUp },
  { id: 'jobs', name: 'Jobs', icon: Wrench },
  { id: 'customers', name: 'Customers', icon: Users },
  { id: 'documents', name: 'Documents', icon: FileText },
  { id: 'diagnostics', name: 'AI Diagnostics', icon: Brain },
  { id: 'inquiries', name: 'Inquiries', icon: Phone },
  { id: 'settings', name: 'Settings', icon: Settings },
];
const TAB_TITLES = Object.fromEntries(TABS.map((t) => [t.id, t.name]));

const resolveTab = (tab) =>
  TAB_ALIASES[tab] || (VALID_TABS.includes(tab) ? tab : 'dashboard');

const JobManagement = ({ defaultTab }) => {
  const [activeTab, setActiveTab] = useState(resolveTab(defaultTab));
  // /jobs, /customers, /invoices and /inquiries render the same tree, so React
  // reuses this instance across them: follow the route's tab when it changes.
  useEffect(() => {
    setActiveTab(resolveTab(defaultTab));
  }, [defaultTab]);
  const [isLoading, setIsLoading] = useState(false);
  const { stats, loadDashboardData } = useJobDashboard({ setIsLoading });
  const {
    jobs,
    jobSearch,
    setJobSearch,
    jobsPage,
    setJobsPage,
    jobsTotalPages,
    loadJobs,
    createJob: createJobHook,
    deleteJob
  } = useJobs({ setIsLoading });
  const [showCreateForm, setShowCreateForm] = useState(false);
  
  // Customer management state
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showCustomerJobs, setShowCustomerJobs] = useState(false);
  const {
    customers,
    customerSearch,
    setCustomerSearch,
    customersPage,
    setCustomersPage,
    customersTotalPages,
    loadCustomers,
    customerJobs,
    loadCustomerJobs,
    customersOverlayLoading
  } = useCustomers({ setIsLoading, setSelectedCustomer, setShowCustomerJobs });
  const [showJobDetails, setShowJobDetails] = useState(false);
  const [selectedJobForDetails, setSelectedJobForDetails] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedJobForActions, setSelectedJobForActions] = useState(null);
  const [showSavedInvoices, setShowSavedInvoices] = useState(false);

  // User preferences state
  const {
    userPreferences,
    setUserPreferences,
    preferencesLoaded,
    savingPreferences,
    loadUserPreferences,
    saveUserPreferences
  } = useMechanicPreferences();

  // Documents tab state
  const {
    allInvoices,
    allEstimates,
    documentsSubTab,
    setDocumentsSubTab,
    documentsPage,
    setDocumentsPage,
    documentsTotalPages,
    documentsLoading,
    loadDocuments,
    savedInvoices,
    loadSavedInvoices,
    downloadSavedInvoicePDF
  } = useDocuments({ setIsLoading, selectedJobForActions, setShowSavedInvoices });

  // Invoice builder flow
  const {
    invoiceData,
    editableInvoice,
    setEditableInvoice,
    editableParts,
    setEditableParts,
    calculatedTotals,
    sendingInvoice,
    openInvoiceModal,
    addPart,
    applyCannedJob,
    updatePart,
    removePart,
    sendInvoiceToCustomer,
    downloadInvoicePDF,
    saveInvoiceToDatabase,
    recordApproval,
    setShareLink,
    refreshInvoiceStatus,
    convertToInvoice
  } = useInvoiceBuilder({
    // Preserves the original guard: saveInvoiceToDatabase only reloaded
    // documents when the documents tab was the active one.
    onInvoiceSaved: () => {
      if (activeTab === 'documents') {
        loadDocuments();
      }
    },
    userPreferences,
    selectedJobForActions,
    setSelectedJobForActions,
    setShowInvoiceModal
  });

  // AI Diagnostics tab state
  const {
    allDiagnostics,
    diagnosticsPage,
    setDiagnosticsPage,
    diagnosticsTotalPages,
    diagnosticsLoading,
    selectedDiagnostic,
    setSelectedDiagnostic,
    loadAllDiagnostics
  } = useDiagnostics();
  const [showDiagnosticDetail, setShowDiagnosticDetail] = useState(false);

  // Inquiries tab state (customer intake, pre-job)
  const inquiries = useInquiries({ setIsLoading });
  const [showQuickIntake, setShowQuickIntake] = useState(false);
  const [convertTarget, setConvertTarget] = useState(null);

  // Close is a bare row action with no overlay to hold an error, and
  // useInquiries.closeInquiry deliberately rejects to its caller (pinned in
  // useInquiries.test.js) so the caller can say something. Handing the raw
  // function to InquiriesTab made that an unhandled rejection: the row did
  // not move, nothing was said, and the mechanic tapped again. The backend
  // has a designed 409 for this path ("Inquiry is already job N and cannot be
  // closed") that had no way to reach the screen.
  //
  // react-hot-toast is the app's transient-feedback mechanism (mounted in
  // App.js, used across payments and auth); messageFromError keeps a 422's
  // list-shaped `detail` out of the message.
  const closeInquiry = async (inquiryId) => {
    try {
      await inquiries.closeInquiry(inquiryId);
    } catch (error) {
      toast.error(
        messageFromError(error, 'Could not close this inquiry. Please try again.')
      );
    }
  };

  // Diagnosis flow (owns selectedJob / diagnosisResult / showDiagnosisModal)
  const {
    showDiagnosisModal,
    setShowDiagnosisModal,
    selectedJob,
    setSelectedJob,
    diagnosisResult,
    viewExistingDiagnosis,
    viewJobDiagnosis,
    runDiagnosis,
    createEstimate,
    diagnosisOverlayLoading
  } = useDiagnosisFlow({
    onDiagnosisComplete: () => loadJobs(),
    setShowJobDetails,
    setSelectedJobForActions,
    openInvoiceModal,
    setEditableParts,
    setEditableInvoice
  });


  // Status update flow
  const {
    statusUpdateForm,
    setStatusUpdateForm,
    payResult,
    updateJobStatus,
    closeStatusModal
  } = useJobStatus({
    onStatusUpdated: () => loadJobs(),
    selectedJobForActions,
    setShowStatusModal
  });


  // Job creation form state
  const [newJob, setNewJob] = useState({
    customer: {
      name: '',
      phone: '',
      email: ''
    },
    vehicle: {
      make: '',
      model: '',
      year: '',
      mileage: '',
      trim: '',
      vin: ''
    },
    symptom_description: '',
    priority: 'normal'
  });

  // Load user preferences on mount
  useEffect(() => {
    loadUserPreferences();
  }, []);
  
  // Load dashboard data
  useEffect(() => {
    if (activeTab === 'dashboard') {
      loadDashboardData();
    } else if (activeTab === 'jobs') {
      loadJobs();
    } else if (activeTab === 'customers') {
      loadCustomers();
    } else if (activeTab === 'documents') {
      loadDocuments();
    } else if (activeTab === 'diagnostics') {
      loadAllDiagnostics();
    } else if (activeTab === 'inquiries') {
      inquiries.loadInquiries();
    }
  }, [activeTab, customersPage, customerSearch, documentsPage, documentsSubTab, diagnosticsPage, jobsPage, inquiries.inquiryStatus, inquiries.inquiriesPage]);
  
  // Debounced job search
  useEffect(() => {
    if (activeTab === 'jobs') {
      const timeoutId = setTimeout(() => {
        setJobsPage(1); // Reset to first page on new search
        loadJobs(jobSearch);
      }, 300); // 300ms debounce
      
      return () => clearTimeout(timeoutId);
    }
  }, [jobSearch]);

  // Close actions dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Presses inside an open dialog are not "outside": the status and invoice
      // modals are mounted behind selectedJobForActions, so clearing it here
      // unmounted the status modal on mousedown, before any click inside it
      // (Update Status included) could land.
      if (
        selectedJobForActions &&
        !event.target.closest('.actions-dropdown') &&
        !event.target.closest('[role="dialog"]') &&
        !showInvoiceModal
      ) {
        setSelectedJobForActions(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [selectedJobForActions, showInvoiceModal]);

  const showJobDetailsModal = (job) => {
    setSelectedJobForDetails(job);
    setShowJobDetails(true);
  };

  const showStatusUpdateModal = (job) => {
    setSelectedJobForActions(job);
    setStatusUpdateForm({
      status: job.job_status,
      completion_date: '',
      // Unticked: emailing the customer is a choice, not a side effect of a status change.
      notify_customer: false,
      customer_notes: ''
    });
    setShowStatusModal(true);
  };

  const resetJobForm = () => {
    setNewJob({
      customer: { name: '', phone: '', email: '' },
      vehicle: { make: '', model: '', year: '', mileage: '', trim: '', vin: '' },
      symptom_description: '',
      priority: 'normal'
    });
  };

  // createJob now lives in useJobs; it needs newJob, activeTab,
  // loadDashboardData (owned by useJobDashboard) and the two form-reset side
  // effects, none of which it can close over from a separate module. This
  // wrapper supplies them at the only call site, preserving the original
  // order: alert -> close form + reset -> conditional refresh.
  const createJob = () =>
    createJobHook(newJob, activeTab, loadDashboardData, () => {
      setShowCreateForm(false);
      resetJobForm();
    });


  return (
    <div>
      {/* Header. Was a second app header ("The CROW", raster logo, shadowed
          card) inside the shell, which already carries the brand; the title
          now names what is on screen, so it agrees with the sidebar item that
          led here. "New Job" is the one solid accent action. */}
      <div className="max-w-7xl mx-auto">
        <PageHeader
          title={
            // Documents holds two sub-tabs; name the one on screen, so the
            // sidebar's "Invoices" arrives at a page titled "Invoices".
            activeTab === 'documents'
              ? (documentsSubTab === 'estimates' ? 'Estimates' : 'Invoices')
              : TAB_TITLES[activeTab]
          }
          className="mb-0"
          action={
            <div className="flex flex-wrap gap-3">
              <Button
                icon={Plus}
                onClick={() => {
                  resetJobForm();
                  setShowCreateForm(true);
                }}
              >
                New Job
              </Button>
              <Button variant="secondary" icon={Phone} onClick={() => setShowQuickIntake(true)}>
                New inquiry
              </Button>
            </div>
          }
        />
      </div>

      {/* Navigation */}
      <div className="max-w-7xl mx-auto">
        <div className="border-b border-border-default">
          <nav className="-mb-px flex gap-4 sm:gap-8 overflow-x-auto scrollbar-hide">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center py-4 px-1 border-b-2 text-body whitespace-nowrap flex-shrink-0 min-h-touch transition-colors duration-state ${
                  activeTab === tab.id
                    ? 'border-accent text-accent font-semibold'
                    : 'border-transparent text-ink-muted font-medium hover:text-ink'
                }`}
              >
                <tab.icon className="h-[18px] w-[18px] mr-1 sm:mr-2" strokeWidth={1.5} aria-hidden="true" />
                <span className="hidden sm:inline">{tab.name}</span>
                <span className="sm:hidden">{tab.name.split(' ')[0]}</span>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto py-6">
        {activeTab === 'dashboard' && (
          <DashboardTab
            stats={stats}
            isLoading={isLoading}
            loadDashboardData={loadDashboardData}
          />
        )}

        {activeTab === 'jobs' && (
          <JobsTab
            isLoading={isLoading}
            jobs={jobs}
            jobSearch={jobSearch}
            setJobSearch={setJobSearch}
            jobsPage={jobsPage}
            setJobsPage={setJobsPage}
            jobsTotalPages={jobsTotalPages}
            loadJobs={loadJobs}
            deleteJob={deleteJob}
            showJobDetailsModal={showJobDetailsModal}
            viewExistingDiagnosis={viewExistingDiagnosis}
            viewJobDiagnosis={viewJobDiagnosis}
            runDiagnosis={runDiagnosis}
            showStatusUpdateModal={showStatusUpdateModal}
            openInvoiceModal={openInvoiceModal}
            setEditableInvoice={setEditableInvoice}
            setEditableParts={setEditableParts}
            selectedJobForActions={selectedJobForActions}
            setSelectedJobForActions={setSelectedJobForActions}
          />
        )}

        {activeTab === 'customers' && (
          <CustomersTab
            stats={stats}
            isLoading={isLoading}
            customers={customers}
            customerSearch={customerSearch}
            setCustomerSearch={setCustomerSearch}
            customersPage={customersPage}
            setCustomersPage={setCustomersPage}
            customersTotalPages={customersTotalPages}
            loadCustomers={loadCustomers}
            loadCustomerJobs={loadCustomerJobs}
          />
        )}

        {activeTab === 'diagnostics' && (
          <DiagnosticsTab
            allDiagnostics={allDiagnostics}
            diagnosticsPage={diagnosticsPage}
            setDiagnosticsPage={setDiagnosticsPage}
            diagnosticsTotalPages={diagnosticsTotalPages}
            diagnosticsLoading={diagnosticsLoading}
            setSelectedDiagnostic={setSelectedDiagnostic}
            loadAllDiagnostics={loadAllDiagnostics}
            setShowDiagnosticDetail={setShowDiagnosticDetail}
          />
        )}
        
        {/* Diagnostic Detail Modal */}
        {showDiagnosticDetail && selectedDiagnostic && (
          <DiagnosticDetailModal
            selectedDiagnostic={selectedDiagnostic}
            setSelectedDiagnostic={setSelectedDiagnostic}
            setShowDiagnosticDetail={setShowDiagnosticDetail}
            laborRate={userPreferences.default_labor_rate}
          />
        )}

        {/* Documents Tab */}
        {activeTab === 'documents' && (
          <DocumentsTab
            allInvoices={allInvoices}
            allEstimates={allEstimates}
            documentsSubTab={documentsSubTab}
            setDocumentsSubTab={setDocumentsSubTab}
            documentsPage={documentsPage}
            setDocumentsPage={setDocumentsPage}
            documentsTotalPages={documentsTotalPages}
            documentsLoading={documentsLoading}
            downloadSavedInvoicePDF={downloadSavedInvoicePDF}
          />
        )}

        {/* Inquiries Tab */}
        {activeTab === 'inquiries' && (
          <InquiriesTab
            inquiries={inquiries.inquiries}
            isLoading={isLoading}
            inquiryStatus={inquiries.inquiryStatus}
            setInquiryStatus={inquiries.setInquiryStatus}
            inquiriesPage={inquiries.inquiriesPage}
            setInquiriesPage={inquiries.setInquiriesPage}
            inquiriesTotalPages={inquiries.inquiriesTotalPages}
            onConvert={(inquiry) => setConvertTarget(inquiry)}
            onCloseInquiry={closeInquiry}
            actionsBusy={inquiries.overlayLoading}
          />
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <SettingsTab
            userPreferences={userPreferences}
            setUserPreferences={setUserPreferences}
            savingPreferences={savingPreferences}
            saveUserPreferences={saveUserPreferences}
          />
        )}
      </div>

      {/* Create Job Modal */}
      {showCreateForm && (
        <CreateJobModal
          isLoading={isLoading}
          setShowCreateForm={setShowCreateForm}
          newJob={newJob}
          setNewJob={setNewJob}
          resetJobForm={resetJobForm}
          createJob={createJob}
        />
      )}

      {/* Enhanced Diagnosis Results Modal */}
      <DiagnosisProgressModal open={diagnosisOverlayLoading} job={selectedJob} />

      <EnhancedDiagnosisModal 
        showModal={showDiagnosisModal}
        onClose={() => setShowDiagnosisModal(false)}
        diagnosisResult={diagnosisResult}
        selectedJob={selectedJob}
        onCreateEstimate={createEstimate}
      />

      {/* Job Details Modal */}
      {showJobDetails && selectedJobForDetails && (
        <JobDetailsModal
          setShowJobDetails={setShowJobDetails}
          selectedJobForDetails={selectedJobForDetails}
          runDiagnosis={runDiagnosis}
          viewExistingDiagnosis={viewExistingDiagnosis}
        />
      )}

      {/* Status Update Modal */}
      {showStatusModal && selectedJobForActions && (
        <StatusUpdateModal
          statusUpdateForm={statusUpdateForm}
          setStatusUpdateForm={setStatusUpdateForm}
          updateJobStatus={updateJobStatus}
          payResult={payResult}
          closeStatusModal={closeStatusModal}
          selectedJobForActions={selectedJobForActions}
        />
      )}

            {/* Invoice Modal */}
      {showInvoiceModal && selectedJobForActions && invoiceData && (
        <InvoiceModal
          setShowInvoiceModal={setShowInvoiceModal}
          selectedJobForActions={selectedJobForActions}
          invoiceData={invoiceData}
          editableInvoice={editableInvoice}
          setEditableInvoice={setEditableInvoice}
          editableParts={editableParts}
          calculatedTotals={calculatedTotals}
          sendingInvoice={sendingInvoice}
          addPart={addPart}
          applyCannedJob={applyCannedJob}
          updatePart={updatePart}
          removePart={removePart}
          sendInvoiceToCustomer={sendInvoiceToCustomer}
          downloadInvoicePDF={downloadInvoicePDF}
          saveInvoiceToDatabase={saveInvoiceToDatabase}
          userPreferences={userPreferences}
          recordApproval={recordApproval}
          setShareLink={setShareLink}
          refreshInvoiceStatus={refreshInvoiceStatus}
          convertToInvoice={convertToInvoice}
        />
      )}

      {/* Customer Jobs Modal */}
      {showCustomerJobs && (
        <CustomerJobsModal
          isLoading={customersOverlayLoading}
          setSelectedJob={setSelectedJob}
          setShowCustomerJobs={setShowCustomerJobs}
          selectedCustomer={selectedCustomer}
          customerJobs={customerJobs}
          setActiveTab={setActiveTab}
        />
      )}

      {/* Quick Intake Modal (new inquiry) */}
      <QuickIntakeModal
        isOpen={showQuickIntake}
        onClose={() => setShowQuickIntake(false)}
        onSubmit={inquiries.createInquiry}
        submitting={inquiries.overlayLoading}
      />

      {/* Convert Inquiry Modal (inquiry -> job) */}
      <ConvertInquiryModal
        inquiry={convertTarget}
        isOpen={Boolean(convertTarget)}
        onClose={() => setConvertTarget(null)}
        onConfirm={inquiries.convertInquiry}
        onUpdate={inquiries.updateInquiry}
        submitting={inquiries.overlayLoading}
      />
    </div>
  );
};

export default JobManagement;